import {format, parse} from 'date-fns';
import type {FactoryArg, FactoryOpts} from 'imask';
import IMask from 'imask/holder';
import 'imask/masked/date';
import {computed, type Ref} from 'vue';
import {useDatePatterns} from '../../common/useDatePatterns';
import type {MaltInputMaskPreset} from '../MaltInputMask.vue';

interface UseDateMaskOptionsProps {
    preset: Ref<MaltInputMaskPreset | undefined>;
    maskOptions: Ref<Partial<FactoryArg>>;
}

/**
 * Regroups all the date mask options
 *
 * Returns the correct mask options based on the preset
 *
 * @param preset - The preset to use
 * @param maskOptions - The mask options to use
 */
export function useDateMaskOptions({preset, maskOptions}: UseDateMaskOptionsProps) {
    const DEFAULT_MIN_DATE = new Date(1900, 1, 1);

    const {dateUnicodePattern, dateMaskPattern, dateMonthYearMaskPattern, dateMonthYearUnicodePattern, dateTimeMaskPattern, dateTimeUnicodePattern} =
        useDatePatterns();

    const minYearDateOptions = computed(() => DEFAULT_MIN_DATE.getFullYear());

    const dateMaskOptions = computed<FactoryArg>(() => {
        let unicodePattern: string = dateUnicodePattern;
        let maskPattern: string = dateMaskPattern;
        let additionalMaskOptions: FactoryOpts = {};

        if (preset.value === 'date-month-year') {
            unicodePattern = dateMonthYearUnicodePattern;
            maskPattern = dateMonthYearMaskPattern;
            additionalMaskOptions = {
                mask: maskPattern,
                pattern: maskPattern,
            };
        } else if (preset.value === 'date-year') {
            // Not localizable
            unicodePattern = 'yyyy';
            maskPattern = 'yyyy';
            additionalMaskOptions = {
                mask: maskPattern,
                pattern: maskPattern,
            };
        } else if (preset.value === 'date-time') {
            unicodePattern = dateTimeUnicodePattern;
            maskPattern = dateTimeMaskPattern;
            additionalMaskOptions = {
                mask: maskPattern,
                pattern: maskPattern,
            };
        } else if (preset.value === 'date' || preset.value === 'date-range') {
            additionalMaskOptions = {
                mask: Date,
                pattern: maskPattern,
            };
        }

        return {
            // min and max date to be passed by parent
            lazy: true,
            autofix: true,
            eager: 'append',
            ...additionalMaskOptions,
            format: (date: any) => format(date, unicodePattern),
            parse: (date: string) => parse(date, unicodePattern, new Date()),
            blocks: {
                dd: {
                    mask: IMask.MaskedRange,
                    from: 1,
                    to: 31,
                    maxLength: 2,
                },
                MM: {
                    mask: IMask.MaskedRange,
                    from: 1,
                    to: 12,
                    maxLength: 2,
                },
                yyyy: {
                    mask: IMask.MaskedRange,
                    from: minYearDateOptions.value,
                    to: 9999,
                    maxLength: 4,
                },
                hh: {
                    mask: IMask.MaskedRange,
                    from: 0,
                    to: 23,
                    maxLength: 2,
                },
                mm: {
                    mask: IMask.MaskedRange,
                    from: 0,
                    to: 59,
                    maxLength: 2,
                },
            },
            ...((maskOptions.value ?? {}) as any),
        } satisfies FactoryArg;
    });

    return {
        dateMaskOptions,
    };
}
